/**
 * The handoff screen (V1-E04-T05): calls agents are handing to people, newest first, each with what the
 * person taking it should know: why, the agent's summary, and what was said. New handoffs appear as they
 * happen (the event stream); the page opens on the recent ones (the event history).
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { type StreamedEvent } from "../api/events";
import { Page, useApiQuery } from "../app/Page";
import { useFormat, useT } from "../i18n";
import { Empty, ErrorState } from "../ui/states";
import { useEventStream } from "./live";

interface Handoff {
  id: string;
  time: string;
  call_id: string;
  to: string;
  mode: string;
  caller: string;
  reason: string;
  summary: string;
  transcript: { role: string; text: string }[];
}

function handoffOf(event: { id: string; time?: string | null; data?: unknown }): Handoff | null {
  const d = (event.data ?? {}) as Record<string, unknown>;
  if (typeof d.call_id !== "string") return null;
  const turns = Array.isArray(d.transcript) ? (d.transcript as { role: string; text: string }[]) : [];
  return {
    id: event.id, time: event.time ?? "", call_id: d.call_id, to: String(d.to ?? ""), mode: String(d.mode ?? ""),
    caller: String(d.caller ?? ""), reason: String(d.reason ?? ""), summary: String(d.summary ?? ""), transcript: turns,
  };  // prettier-ignore
}

export function HandoffsPage() {
  const t = useT();
  const format = useFormat();
  const recent = useApiQuery("handoffs", (api) =>
    api.GET("/v1/events", { params: { query: { types: "call.handoff.context", limit: 20 } } }),
  );
  const [live, setLive] = useState<Handoff[]>([]);
  useEventStream({ types: ["call.handoff.context"] }, (event: StreamedEvent) => {
    const h = handoffOf(event);
    if (h) setLive((list) => (list.some((x) => x.id === h.id) ? list : [h, ...list]));
  });
  const earlier = ((recent.data as { data?: unknown[] } | undefined)?.data ?? [])
    .map((e) => handoffOf(e as { id: string; time?: string; data?: unknown }))
    .filter((h): h is Handoff => h !== null && !live.some((x) => x.id === h.id));
  const all = [...live, ...earlier];

  return (
    <Page title={t("handoffs.title")}>
      <p className="mb-4 text-sm text-[var(--color-muted)]">{t("handoffs.help")}</p>
      {recent.isError && <ErrorState error={recent.error} onRetry={() => void recent.refetch()} />}
      {!recent.isPending && all.length === 0 && <Empty title={t("handoffs.none")} />}
      <ol className="space-y-4" aria-label={t("handoffs.title")}>
        {all.map((h) => (
          <li key={h.id} className="space-y-2 rounded-lg border border-[var(--color-border)] p-4">
            <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="font-semibold">{h.caller}</span>
              <span className="text-[var(--color-muted)]">→ {h.to} ({t(`handoffs.mode.${h.mode === "warm" ? "warm" : "blind"}`)})</span>
              {h.time && <time dateTime={h.time} className="text-[var(--color-muted)]">{format.dateTime(h.time)}</time>}
              <Link to="/calls/$callId" params={{ callId: h.call_id }} className="underline text-[var(--color-primary)]">
                {t("handoffs.call")}
              </Link>
            </div>
            {h.reason && <p><span className="font-medium">{t("handoffs.reason")}: </span>{h.reason}</p>}
            {h.summary && <p>{h.summary}</p>}
            {h.transcript.length > 0 && (
              <details>
                <summary className="cursor-pointer text-sm">{t("handoffs.transcript", { n: String(h.transcript.length) })}</summary>
                <ol className="mt-2 space-y-1 text-sm">
                  {h.transcript.map((turn, i) => (
                    <li key={i}><span className="font-medium">{t(`handoffs.role.${turn.role === "user" ? "user" : "assistant"}`)}: </span>{turn.text}</li>
                  ))}
                </ol>
              </details>
            )}
          </li>
        ))}
      </ol>
    </Page>
  );
}
